<template>
  <transition name="modal-scale" appear>
    <div v-if="open" class="fixed inset-0 z-[120] flex items-center justify-center">
      <div class="absolute inset-0 bg-slate-950/80 backdrop-blur-md" @click="emit('close')" />
      <form
        class="relative w-[480px] rounded-2xl bg-slate-950/90 border border-slate-700/70 shadow-2xl p-6 space-y-4"
        @submit.prevent="onSubmit"
      >
        <header class="flex items-start justify-between">
          <div>
            <p class="text-xs uppercase tracking-[0.3em] text-slate-500">
              {{ mode === 'create' ? 'Add product' : 'Edit product' }}
            </p>
            <h2 class="text-xl font-semibold text-white mt-1">Product Details</h2>
          </div>
          <button type="button" class="text-slate-400 hover:text-white" @click="emit('close')">
            Close
          </button>
        </header>

        <div class="space-y-3">
          <label class="block">
            <span class="text-xs text-slate-400 uppercase tracking-[0.2em]">Name</span>
            <input
              v-model="form.name"
              type="text"
              class="input-dark mt-1 w-full"
              placeholder="POTS Replacement"
              required
            />
          </label>

          <label class="block">
            <span class="text-xs text-slate-400 uppercase tracking-[0.2em]">Code</span>
            <input
              v-model="form.code"
              type="text"
              class="input-dark mt-1 w-full"
              placeholder="pots-repl"
              required
            />
          </label>

          <label class="block">
            <span class="text-xs text-slate-400 uppercase tracking-[0.2em]">Description</span>
            <textarea
              v-model="form.description"
              class="input-dark mt-1 w-full min-h-[90px]"
              placeholder="Short description"
            ></textarea>
          </label>

          <label class="inline-flex items-center gap-2">
            <input type="checkbox" v-model="form.isActive" class="form-checkbox--glass" />
            <span class="text-sm text-slate-300">Active</span>
          </label>
        </div>

        <footer class="flex justify-end gap-2">
          <button type="button" class="btn-secondary" @click="emit('close')">Cancel</button>
          <button type="submit" class="btn-primary" :disabled="saving">
            {{ saving ? 'Saving…' : mode === 'create' ? 'Create' : 'Save changes' }}
          </button>
        </footer>
      </form>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from 'vue'
import type {
  EquipmentProduct,
  EquipmentProductCreateInput,
  EquipmentProductUpdateInput,
} from '@/types/inventory'

const props = defineProps<{
  open: boolean
  mode: 'create' | 'edit'
  product: EquipmentProduct | null
  saving: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'submit', payload: EquipmentProductCreateInput | EquipmentProductUpdateInput): void
}>()

const form = reactive<EquipmentProductCreateInput>({
  name: '',
  code: '',
  description: '',
  isActive: true,
})

watch(
  () => props.product,
  (product) => {
    if (!product) {
      form.name = ''
      form.code = ''
      form.description = ''
      form.isActive = true
    } else {
      form.name = product.name
      form.code = product.code
      form.description = product.description ?? ''
      form.isActive = product.isActive
    }
  },
  { immediate: true },
)

const payload = computed(
  (): EquipmentProductCreateInput => ({
    name: form.name.trim(),
    code: form.code.trim(),
    description: form.description?.trim() || null,
    isActive: form.isActive,
  }),
)

function onSubmit() {
  emit('submit', payload.value)
}
</script>
